import type { TrolleyQuantity } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/TrolleyControls/types';

import type { CachedTrolley, CachedTrolleyItem } from './useTrolleyService';

const updateTrolleyItem = (
  old: CachedTrolley,
  lineNumber: string,
  item: CachedTrolleyItem | undefined,
): CachedTrolley => {
  if (!item) {
    return {
      ...old,
      trolleyItems: old.trolleyItems.filter(oldItem => oldItem.lineNumber !== lineNumber),
    };
  }

  const itemExists = old.trolleyItems.some(oldItem => oldItem.lineNumber === lineNumber);

  return {
    ...old,
    trolleyItems: itemExists
      ? old.trolleyItems.map(oldItem => (oldItem.lineNumber === lineNumber ? item : oldItem))
      : [...old.trolleyItems, item],
  };
};

export const updateTrolleyCache = (
  old: CachedTrolley | undefined,
  lineNumber: string,
  quantity: TrolleyQuantity,
): CachedTrolley | undefined => {
  if (!old) return old;

  if (quantity.amount === 0) {
    return updateTrolleyItem(old, lineNumber, undefined);
  }

  const existingItem = old.trolleyItems.find(oldItem => oldItem.lineNumber === lineNumber);

  return updateTrolleyItem(old, lineNumber, {
    lineNumber,
    quantity,
    trolleyItemId: existingItem?.trolleyItemId ?? `optimistic-${lineNumber}`,
  });
};

export const revertTrolleyItem = (
  old: CachedTrolley | undefined,
  lineNumber: string,
  previousItem: CachedTrolleyItem | undefined,
): CachedTrolley | undefined => {
  if (!old) return old;

  return updateTrolleyItem(old, lineNumber, previousItem);
};

// Replaces placeholder trolleyItemId with real trolleyItemId once returned from API
export const patchTrolleyItemId = (
  old: CachedTrolley | undefined,
  lineNumber: string,
  trolleyItemId: string,
): CachedTrolley | undefined => {
  if (!old) return old;

  return {
    ...old,
    trolleyItems: old.trolleyItems.map(item =>
      item.lineNumber === lineNumber ? { ...item, trolleyItemId } : item,
    ),
  };
};
